import React, { useEffect, useState } from 'react';
import { pickMovieRecommendations } from '../api/movies';
import { Movie } from '../Movie';
import { Button } from './Button';
import { Flex } from './Flex';
import { Input } from './Input';
import { Popover } from './Popover';

export function PickMovieModal({ isOpen, onClose }) {
  const STORAGE_KEY = 'pickMovieSettings';
  const savedSettings = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');

  const [keywords, setKeywords] = useState('');
  const [externalRating, setExternalRating] = useState(
    savedSettings.externalRating ?? '',
  );
  const [wishlistOnly, setWishlistOnly] = useState(false);
  const [limit, setLimit] = useState(savedSettings.limit ?? '5');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null); // null = not searched yet
  const [error, setError] = useState('');

  useEffect(() => {
    if (!isOpen) {
      setKeywords('');
      setWishlistOnly(false);
      setResults(null);
      setError('');
    }
  }, [isOpen]);

  const handleExternalRatingChange = (e) => {
    const val = e.target.value;
    setExternalRating(val);
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...saved, externalRating: val }),
    );
  };

  const handleLimitChange = (e) => {
    const val = e.target.value;
    setLimit(val);
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ ...saved, limit: val }));
  };

  const handlePick = async () => {
    setError('');
    setLoading(true);
    setResults(null);
    try {
      const userJson = localStorage.getItem('user');
      if (!userJson) throw new Error('Invalid user');
      const user = JSON.parse(userJson);

      const params = {
        username: user.username,
        keywords:
          keywords && keywords.trim().length > 0 ? keywords.trim() : null,
        externalRating: externalRating !== '' ? Number(externalRating) : null,
        wishlistOnly,
        limit: Math.min(5, Math.max(1, parseInt(limit) || 5)),
      };

      const recs = await pickMovieRecommendations(params);
      setResults(recs || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch recommendations');
    } finally {
      setLoading(false);
    }
  };

  const hasResults = Array.isArray(results) && results.length > 0;
  const searched = results !== null;

  return (
    <Popover open={isOpen} onClose={onClose} modal={true} showBackdrop={true}>
      <div className="pick-movie-modal">
        <div className="pick-movie-modal__header">
          <h2>Pick a movie for me</h2>
          <p className="pick-movie-modal__subtitle">
            Get suggestions from your collection
          </p>
        </div>

        <div className="pick-movie-modal__form">
          <Input
            label="Keywords (actor, genre or title)"
            value={keywords}
            onChange={(e) => setKeywords(e.target.value)}
            placeholder="e.g. Tom Hanks, comedy"
            onKeyDown={(e) => e.key === 'Enter' && handlePick()}
          />

          <Flex direction="row" gap="1rem" align="flex-end">
            <Input
              label="Minimum external rating (0–100)"
              value={externalRating}
              onChange={handleExternalRatingChange}
              placeholder="e.g. 70"
              type="number"
              min={0}
              max={100}
            />
            <Input
              label="How many? (1–5)"
              value={limit}
              onChange={handleLimitChange}
              type="number"
              min={1}
              max={5}
            />
          </Flex>

          <label className="pick-movie-modal__toggle">
            <input
              type="checkbox"
              checked={wishlistOnly}
              onChange={(e) => setWishlistOnly(e.target.checked)}
            />
            <span>Wishlist exclusive</span>
          </label>

          {error && <div className="pick-movie-modal__error">{error}</div>}
        </div>

        <Flex
          direction="row"
          gap="1rem"
          justify="flex-end"
          className="pick-movie-modal__actions"
        >
          <Button variant="secondary" onClick={onClose} disabled={loading}>
            Close
          </Button>
          <Button onClick={handlePick} disabled={loading}>
            {loading ? 'Searching…' : 'Find movies'}
          </Button>
        </Flex>

        {(hasResults || (searched && !loading)) && (
          <div className="pick-movie-modal__results">
            <h3 className="pick-movie-modal__results-title">
              {hasResults
                ? `${results.length} suggestion${results.length === 1 ? '' : 's'}`
                : 'No movies found'}
            </h3>
            {hasResults && (
              <div className="pick-movie-modal__scroll">
                {results.map((m) => (
                  <Movie key={m.id} movie={m} size="regular" />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Popover>
  );
}
